import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listTracks, setRollout, haltTrack, resumeTrack, completeTrack, promoteTrack, uploadBuild,
  setReleaseNotes, RELEASE_NOTES_MAX,
  type Track, type TrackRelease, type ReleaseStatus, type BuildKind,
} from '@/api/play';
import { Button } from '../Button';
import { Select } from '../Select';
import { Modal } from '../Modal';
import { ReviewNotice } from './ReviewNotice';
import { ConfirmModal } from './ConfirmModal';
import { ReleaseNotesEditor, toNoteDrafts, type NoteTexts } from './ReleaseNotesEditor';

const STANDARD_TRACKS = ['production', 'beta', 'alpha', 'internal'];

function activeRelease(t: Track): TrackRelease | undefined {
  return (t.releases ?? []).find((r) => r.status !== 'completed') ?? t.releases?.[0];
}

const STATUS_BADGE: Record<ReleaseStatus, string> = {
  draft: 'bg-surface-muted text-content-muted',
  inProgress: 'bg-blue-500/15 text-blue-400',
  halted: 'bg-amber-500/15 text-amber-400',
  completed: 'bg-green-500/15 text-green-400',
};

type TrackAction = 'rollout' | 'promote' | 'halt' | 'resume' | 'complete';
type Dialog = { kind: TrackAction; track: string } | null;

export function PlayTracksPanel({ appId, appName, releaseNotesKeywords }: {
  appId: number;
  appName?: string | null;
  releaseNotesKeywords?: string | null;
}) {
  const qc = useQueryClient();
  const { data: tracks = [], isLoading, error, refetch } = useQuery({
    queryKey: ['play-tracks', appId],
    queryFn: () => listTracks(appId),
  });
  const [dialog, setDialog] = useState<Dialog>(null);
  const [completedRelease, setCompletedRelease] = useState<UploadedRelease | null>(null);

  const done = () => {
    setDialog(null);
    qc.invalidateQueries({ queryKey: ['play-tracks', appId] });
  };

  if (isLoading) return <p className="text-content-muted text-sm">Loading tracks…</p>;
  if (error) {
    return (
      <div className="bg-surface border border-border rounded-lg p-6 text-center">
        <p className="text-sm text-red-400 mb-3">{(error as Error).message}</p>
        <Button variant="secondary" size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ReviewNotice variant="release" />
      <BuildUploader
        appId={appId}
        appName={appName}
        releaseNotesKeywords={releaseNotesKeywords}
        tracks={tracks}
        onUploaded={() => qc.invalidateQueries({ queryKey: ['play-tracks', appId] })}
      />
      {tracks.length === 0 ? (
        <p className="text-content-muted text-sm">No tracks.</p>
      ) : (
        <div className="space-y-3">
          {tracks.map((t) => (
            <TrackCard
              key={t.track}
              track={t}
              onAction={(kind) => setDialog({ kind, track: t.track })}
              onAddNotes={setCompletedRelease}
            />
          ))}
        </div>
      )}

      {dialog?.kind === 'rollout' && (
        <RolloutModal appId={appId} track={dialog.track} onDone={done} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'promote' && (
        <PromoteModal appId={appId} fromTrack={dialog.track} tracks={tracks} onDone={done} onClose={() => setDialog(null)} />
      )}
      {dialog && (dialog.kind === 'halt' || dialog.kind === 'resume' || dialog.kind === 'complete') && (
        <TrackActionConfirm
          appId={appId}
          track={dialog.track}
          kind={dialog.kind}
          onDone={(updatedTrack) => {
            if (dialog.kind === 'complete') {
              setCompletedRelease(releaseNotesTarget(updatedTrack));
            }
            done();
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {completedRelease && (
        <ReleaseNotesStep
          appId={appId}
          appName={appName}
          releaseNotesKeywords={releaseNotesKeywords}
          release={completedRelease}
          onAttached={() => {
            setCompletedRelease(null);
            qc.invalidateQueries({ queryKey: ['play-tracks', appId] });
          }}
          onSkip={() => setCompletedRelease(null)}
        />
      )}
    </div>
  );
}

function TrackCard({ track, onAction, onAddNotes }: {
  track: Track;
  onAction: (kind: TrackAction) => void;
  onAddNotes: (release: UploadedRelease) => void;
}) {
  const rel = activeRelease(track);
  const status = rel?.status;
  const notesTarget = releaseNotesTarget(track);
  const noteCount = notesTarget?.releaseNotes ? Object.keys(notesTarget.releaseNotes).length : 0;
  const pct = rel?.userFraction != null
    ? `${Math.round(rel.userFraction * 100)}%`
    : status === 'completed' ? '100%' : null;

  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-content">{track.track}</span>
        {status && (
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[status]}`}>{status}</span>
        )}
        {pct && <span className="text-xs text-content-muted">{pct} of users</span>}
      </div>
      <div className="text-xs text-content-muted mt-1">
        Version codes: {(rel?.versionCodes ?? []).join(', ') || '—'}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button size="sm" variant="secondary" disabled={status !== 'inProgress'} onClick={() => onAction('rollout')}>Set rollout %</Button>
        <Button size="sm" variant="secondary" disabled={status !== 'inProgress'} onClick={() => onAction('halt')}>Halt</Button>
        <Button size="sm" variant="secondary" disabled={status !== 'halted'} onClick={() => onAction('resume')}>Resume</Button>
        <Button size="sm" variant="secondary" disabled={!rel || status === 'completed'} onClick={() => onAction('complete')}>Release to 100%</Button>
        <Button size="sm" variant="secondary" disabled={!rel} onClick={() => onAction('promote')}>Promote…</Button>
        {notesTarget && (
          <Button size="sm" variant="secondary" onClick={() => onAddNotes(notesTarget)}>
            {noteCount > 0 ? `Edit What’s new (${noteCount})` : 'Add What’s new'}
          </Button>
        )}
      </div>
    </div>
  );
}

const ACTION_COPY: Record<'halt' | 'resume' | 'complete', {
  title: string; confirm: string; message: (track: string) => string;
}> = {
  halt: {
    title: 'Halt rollout', confirm: 'Halt',
    message: (t) => `Halt the staged rollout on “${t}”? Users who already received the update keep it; no new users get it until you resume.`,
  },
  resume: {
    title: 'Resume rollout', confirm: 'Resume',
    message: (t) => `Resume the staged rollout on “${t}” at its previous percentage?`,
  },
  complete: {
    title: 'Release to 100%', confirm: 'Release to 100%',
    message: (t) => `Release the current version on “${t}” to all users? A completed release cannot be staged again.`,
  },
};

function TrackActionConfirm({ appId, track, kind, onDone, onClose }: {
  appId: number; track: string; kind: 'halt' | 'resume' | 'complete';
  onDone: (updatedTrack: Track) => void; onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () =>
      kind === 'halt' ? haltTrack(appId, track)
        : kind === 'resume' ? resumeTrack(appId, track)
          : completeTrack(appId, track),
    onSuccess: onDone,
    onError: (e: any) => setError(e?.message ?? 'failed'),
  });
  const copy = ACTION_COPY[kind];
  return (
    <ConfirmModal
      isOpen title={copy.title} confirmLabel={copy.confirm} danger={kind === 'halt'}
      loading={mut.isPending} error={error} message={copy.message(track)}
      onConfirm={() => mut.mutate()} onClose={onClose}
    />
  );
}

function PercentInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="number" min={1} max={99} value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-md bg-background border border-border text-content text-sm"
    />
  );
}

function validPercent(v: string): boolean {
  const p = Number(v);
  return Number.isFinite(p) && p >= 1 && p <= 99;
}

function RolloutModal({ appId, track, onDone, onClose }: {
  appId: number; track: string; onDone: () => void; onClose: () => void;
}) {
  const [percent, setPercent] = useState('10');
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => setRollout(appId, track, Number(percent) / 100),
    onSuccess: onDone,
    onError: (e: any) => setError(e?.message ?? 'failed'),
  });
  return (
    <Modal isOpen onClose={onClose} title={`Set rollout % — ${track}`} size="sm">
      <label className="block text-sm text-content-muted mb-1">Percentage of users (1–99)</label>
      <PercentInput value={percent} onChange={setPercent} />
      <p className="text-xs text-content-muted mt-1">Applies immediately; the update reaches this share of users on the track.</p>
      {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button disabled={!validPercent(percent)} loading={mut.isPending} onClick={() => mut.mutate()}>Apply</Button>
      </div>
    </Modal>
  );
}

function PromoteModal({ appId, fromTrack, tracks, onDone, onClose }: {
  appId: number; fromTrack: string; tracks: Track[]; onDone: () => void; onClose: () => void;
}) {
  const targets = Array.from(new Set([...STANDARD_TRACKS, ...tracks.map((t) => t.track)]))
    .filter((t) => t !== fromTrack);
  const [toTrack, setToTrack] = useState(targets.includes('production') ? 'production' : targets[0] ?? '');
  const [mode, setMode] = useState<'full' | 'staged'>('full');
  const [percent, setPercent] = useState('10');
  const [error, setError] = useState<string | null>(null);

  const valid = !!toTrack && (mode === 'full' || validPercent(percent));

  const mut = useMutation({
    mutationFn: () => promoteTrack(
      appId, fromTrack, toTrack,
      mode === 'full' ? { type: 'full' } : { type: 'staged', fraction: Number(percent) / 100 },
    ),
    onSuccess: onDone,
    onError: (e: any) => setError(e?.message ?? 'failed'),
  });

  return (
    <Modal isOpen onClose={onClose} title={`Promote ${fromTrack}`} size="sm">
      <p className="text-sm text-content-muted mb-3">
        Copies the active release from “{fromTrack}” to the target track and starts the rollout there.
      </p>
      <label className="block text-sm text-content-muted mb-1">Target track</label>
      <Select value={toTrack} onChange={setToTrack} options={targets.map((t) => ({ value: t, label: t }))} className="mb-3" />
      <label className="block text-sm text-content-muted mb-1">Rollout</label>
      <Select
        value={mode}
        onChange={(v) => setMode(v as 'full' | 'staged')}
        options={[
          { value: 'full', label: 'Full — all users at once' },
          { value: 'staged', label: 'Staged — a percentage of users' },
        ]}
        className="mb-3"
      />
      {mode === 'staged' && (
        <div className="mb-3">
          <label className="block text-sm text-content-muted mb-1">Percentage of users (1–99)</label>
          <PercentInput value={percent} onChange={setPercent} />
        </div>
      )}
      {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button disabled={!valid} loading={mut.isPending} onClick={() => mut.mutate()}>Promote</Button>
      </div>
    </Modal>
  );
}

const ROLLOUT_HINTS: Record<'draft' | 'staged' | 'full', string> = {
  draft: 'Uploads without releasing — release it later from Play Console or by promoting.',
  staged: 'Releases to the given share of users immediately after upload.',
  full: 'Releases to all users on the track immediately after upload.',
};

export type UploadedRelease = {
  versionCode: number | string;
  track: string;
  rollout: string;
  releaseNotes?: NoteTexts;
};

/** Build the post-release prompt from the exact Track returned by Play. */
export function releaseNotesTarget(track: Track): UploadedRelease | null {
  const release = (track.releases ?? []).find((item) => item.status === 'completed');
  const versionCode = release?.versionCodes?.[0];
  if (!versionCode) return null;
  const releaseNotes = release?.releaseNotes?.length
    ? Object.fromEntries(release.releaseNotes.map((note) => [note.language, note.text]))
    : undefined;
  return { versionCode, track: track.track, rollout: 'completed', ...(releaseNotes ? { releaseNotes } : {}) };
}

function BuildUploader({ appId, appName, releaseNotesKeywords, tracks, onUploaded }: {
  appId: number;
  appName?: string | null;
  releaseNotesKeywords?: string | null;
  tracks: Track[];
  onUploaded: () => void;
}) {
  const trackOptions = Array.from(new Set([...tracks.map((t) => t.track), ...STANDARD_TRACKS]));
  const [track, setTrack] = useState(trackOptions[0] ?? 'production');
  const [kind, setKind] = useState<BuildKind>('aab');
  const [rolloutType, setRolloutType] = useState<'draft' | 'staged' | 'full'>('draft');
  const [percent, setPercent] = useState('10');
  const [file, setFile] = useState<File | null>(null);
  const [uploadedRelease, setUploadedRelease] = useState<UploadedRelease | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async (): Promise<UploadedRelease> => {
      const rolloutParam = rolloutType === 'staged' ? String(Number(percent) / 100) : rolloutType;
      return uploadBuild(appId, track, kind, rolloutParam, file!);
    },
    onSuccess: (out) => {
      setResult(`Uploaded versionCode ${out.versionCode} to ${out.track} (${out.rollout})`);
      setFile(null);
      setError(null);
      setUploadedRelease(out);
      onUploaded();
    },
    onError: (e: any) => setError(e?.message ?? 'Upload failed'),
  });

  const valid = !!file && !!track && (rolloutType !== 'staged' || validPercent(percent));

  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <h4 className="text-sm font-medium text-content mb-3">Upload build</h4>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={track} onChange={setTrack} options={trackOptions.map((t) => ({ value: t, label: t }))} size="sm" className="w-36" />
        <Select value={kind} onChange={(v) => setKind(v as BuildKind)} options={[{ value: 'aab', label: 'AAB' }, { value: 'apk', label: 'APK' }]} size="sm" className="w-24" />
        <Select
          value={rolloutType}
          onChange={(v) => setRolloutType(v as 'draft' | 'staged' | 'full')}
          options={[
            { value: 'draft', label: 'Draft' },
            { value: 'staged', label: 'Staged %' },
            { value: 'full', label: 'Full (100%)' },
          ]}
          size="sm" className="w-32"
        />
        {rolloutType === 'staged' && (
          <input
            type="number" min={1} max={99} value={percent}
            onChange={(e) => setPercent(e.target.value)}
            className="w-20 px-2 py-1.5 rounded-md bg-background border border-border text-content text-sm"
          />
        )}
        <label className="px-3 py-1.5 rounded-md bg-background border border-border text-sm text-content-muted cursor-pointer hover:text-content max-w-[14rem] truncate">
          <input type="file" accept=".aab,.apk" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          {file ? file.name : 'Choose file…'}
        </label>
        <Button size="sm" disabled={!valid} loading={mut.isPending} onClick={() => mut.mutate()}>Upload</Button>
      </div>
      <p className="text-xs text-content-muted mt-2">{ROLLOUT_HINTS[rolloutType]}</p>
      {result && <p className="text-green-400 text-xs mt-2">{result}</p>}
      {error && <p className="text-red-400 text-xs mt-2">{error}</p>}

      {uploadedRelease && (
        <ReleaseNotesStep
          appId={appId}
          appName={appName}
          releaseNotesKeywords={releaseNotesKeywords}
          release={uploadedRelease}
          onAttached={(noteCount) => {
            setResult(
              `Uploaded versionCode ${uploadedRelease.versionCode} to ${uploadedRelease.track} (${uploadedRelease.rollout})` +
              ` · What’s new in ${noteCount} locales`,
            );
            setUploadedRelease(null);
            onUploaded();
          }}
          onSkip={() => setUploadedRelease(null)}
        />
      )}
    </div>
  );
}

/**
 * The build has already been uploaded when this step opens. Keeping release
 * notes in their own screen makes the target track/version explicit and lets
 * the operator retry a failed notes request without uploading the binary again.
 */
function ReleaseNotesStep({ appId, appName, releaseNotesKeywords, release, onAttached, onSkip }: {
  appId: number;
  appName?: string | null;
  releaseNotesKeywords?: string | null;
  release: UploadedRelease;
  onAttached: (noteCount: number) => void;
  onSkip: () => void;
}) {
  const [notes, setNotes] = useState<NoteTexts>(() => ({ ...release.releaseNotes }));
  const [error, setError] = useState<string | null>(null);
  const drafts = toNoteDrafts(notes, Object.keys(notes));
  const notesOverLimit = Object.values(notes).some((text) => text.length > RELEASE_NOTES_MAX);

  const mut = useMutation({
    mutationFn: () => setReleaseNotes(appId, release.track, release.versionCode, drafts),
    onSuccess: () => onAttached(drafts.length),
    onError: (e: any) => setError(e?.message ?? 'Failed to attach What’s new'),
  });

  return (
    <Modal isOpen onClose={onSkip} title="Add What’s new" size="lg">
      <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2">
        <p className="text-sm font-medium text-green-400">Build uploaded successfully</p>
        <p className="text-xs text-content-muted mt-0.5">
          {release.track} · versionCode {release.versionCode} · {release.rollout}
        </p>
      </div>
      <p className="text-sm text-content-muted mt-3">
        Now add the release notes users will see for this version. This step is optional.
      </p>

      <ReleaseNotesEditor
        appId={appId}
        appName={appName}
        keywords={releaseNotesKeywords}
        value={notes}
        onChange={setNotes}
        disabled={mut.isPending}
      />

      {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="secondary" disabled={mut.isPending} onClick={onSkip}>Skip for now</Button>
        <Button
          disabled={drafts.length === 0 || notesOverLimit}
          loading={mut.isPending}
          onClick={() => { setError(null); mut.mutate(); }}
        >
          Attach What’s new
        </Button>
      </div>
    </Modal>
  );
}
